import React, { useState } from 'react';
import Button from '../../../components/ui/buttons/Button';
import ProspectRow from './ProspectRow';
import {
  ArrowDownNarrowWide,
  ChevronDown,
  ChevronUp,
  CirclePlus,
  ListFilter,
  UserIcon,
  UserRoundPlus,
} from 'lucide-react';
import ProspectFilters from './ProspectFilters';
import Pagination from '../../../components/ui/pagination/Pagination';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import CreateProspectModal from '../../../components/modals/CreateProspectModal';
import EditProspectModal from '../../../components/modals/EditProspectModal';
import EditPlanModal from '../../../components/modals/EditPlanModal';

const Prospects = ({ clients: initialClients, pagination: initialPagination }) => {
  const [clients, setClients] = useState(initialClients);
  const [pagination, setPagination] = useState(initialPagination);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    planner_id: '',
    bt_profile: '',
  });
  const [showCreateProspectModal, setShowCreateProspectModal] = useState(false);
  const [selectedClientForPlan, setSelectedClientForPlan] = useState(null);
  const [editClientId, setEditClientId] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const buildQuery = (params) =>
    Object.entries(params)
      .filter(([, v]) => v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');

  const fetchData = (page = 1, filterParams = filters, sort = sortBy, order = sortOrder) => {
    const query = buildQuery({ ...filterParams, page, sort, order });

    fetch(`/app/planner/prospects?${query}`)
      .then((res) => res.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const data = doc.querySelector('#app-root')?.dataset?.props;
        const parsed = camelizeKeys(JSON.parse(data));

        setClients(parsed.clients);
        setPagination(parsed.pagination);
      });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchData(1, newFilters);
  };

  const handlePageChange = (newPage) => {
    fetchData(newPage);
  };

  const handleSortClick = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
    fetchData(1, filters, field, newOrder);
  };

  const handleOrderToggle = () => {
    const nextSortBy = sortBy === 'name' ? 'created_at' : 'name';
    const nextSortOrder = nextSortBy === 'name' ? 'asc' : 'desc';
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    fetchData(1, filters, nextSortBy, nextSortOrder);
  };

  const handleModalSuccess = () => {
    setShowCreateProspectModal(false);
    setEditClientId(null);
    setSelectedClientForPlan(null);
    fetchData();
  };

  const getColumnLabel = (column) => {
    switch (column) {
      case 'name':
        return 'nome';
      case 'email':
        return 'e-mail';
      case 'phone':
        return 'telefone';
      case 'created_at':
        return 'data de cadastro';
      default:
        return '';
    }
  };

  const renderSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? (
      <ChevronUp size={14} className="text-tealMedium ml-1" />
    ) : (
      <ChevronDown size={14} className="text-tealMedium ml-1" />
    );
  };

  return (
    <div className="h-full flex flex-col gap-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h2 className="px-4 text-2xl font-semibold text-blueDarker flex items-center gap-2 font-jost">
          <UserRoundPlus strokeWidth={1.25} className="w-6 h-6 text-tealMedium" />
          Cadastro de clientes
        </h2>

        <Button onClick={() => setShowCreateProspectModal(true)} icon={CirclePlus}>
          CADASTRAR NOVO CLIENTE
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <p className="px-4 text-lg font-semibold text-blueDarker">Clientes em processo de cadastro</p>
        <ProspectFilters filters={filters} onChange={handleFilterChange} />
      </div>

      <table className="w-full text-left table-fixed">
        <thead>
          <tr>
            <th className="pl-4 py-2 w-1/5">
              <button
                onClick={() => handleSortClick('name')}
                className="flex items-center hover:opacity-80 text-blueDarker font-semibold text-base"
              >
                Nome
                {renderSortIcon('name')}
              </button>
            </th>
            <th className="pl-5 py-2 w-1/5">
              <button
                onClick={() => handleSortClick('email')}
                className="flex items-center hover:opacity-80 text-blueDarker font-semibold text-base"
              >
                E-mail
                {renderSortIcon('email')}
              </button>
            </th>
            <th className="py-2">
              <button
                onClick={() => handleSortClick('phone')}
                className="flex items-center hover:opacity-80 text-blueDarker font-semibold text-base"
              >
                Telefone
                {renderSortIcon('phone')}
              </button>
            </th>
            <th className="py-2">
              <button
                onClick={() => handleSortClick('created_at')}
                className="flex items-center hover:opacity-80 text-blueDarker font-semibold text-base"
              >
                Cadastrado em
                {renderSortIcon('created_at')}
              </button>
            </th>
            <th className="pr-4 py-2" style={{ width: '560px', minWidth: '520px' }}>
              <div className="flex justify-end">
                <button onClick={handleOrderToggle} className="flex items-center hover:opacity-80 gap-1">
                  <ArrowDownNarrowWide strokeWidth={1.5} size={20} className="text-tealMedium" />
                  <span className="text-blueDarker text-base">
                    <span className="font-semibold">Ordenar por:</span> {getColumnLabel(sortBy)}
                  </span>
                </button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {clients.map((client, index) => (
            <ProspectRow
              key={client.id}
              client={client}
              index={index}
              onEdit={() => setEditClientId(client.id)}
              onEditPlan={() => setSelectedClientForPlan(client)}
              onSuccess={handleModalSuccess}
            />
          ))}
        </tbody>
      </table>

      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />

      {showCreateProspectModal && (
        <CreateProspectModal onClose={() => setShowCreateProspectModal(false)} onSuccess={handleModalSuccess} />
      )}
      {selectedClientForPlan && (
        <EditPlanModal
          onClose={() => setSelectedClientForPlan(null)}
          clientName={selectedClientForPlan.name}
          clientId={selectedClientForPlan.id}
          onSuccess={handleModalSuccess}
        />
      )}
      {editClientId && (
        <EditProspectModal
          clientId={editClientId}
          onClose={() => setEditClientId(null)}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default Prospects;
